import { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  ZoomControl,
  useMap,
} from "react-leaflet";
import type { LatLngExpression, PathOptions } from "leaflet";
import { useFormat } from "../../context/FormatContext";
import axios from "axios";
import "../styles/dashboard.css";
import "leaflet/dist/leaflet.css";

interface AdminUser {
  id?: number;
  name?: string;
  group_id?: number;
}

interface SalesOverTimeItem {
  month: string;
  sales: number;
}

interface TopSellingProductItem {
  name: string;
  sold: number;
}

interface RevenueByCategoryItem {
  name: string;
  value: number;
}

interface CustomerReviewItem {
  subscriberId: string;
  subscriberName: string;
  productName: string;
  review: string;
}

interface TrafficVisitorItem {
  ipAddress: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  counter: number;
  visitedPages?: string[];
  lastVisitedAt?: string;
}

interface DashboardData {
  dashboardScope?: "admin" | "vendor";
  vendorId?: number | null;

  todaysOrder: number;
  totalOrders: number;
  todaysRevenue: number;
  totalRevenue: number;

  totalProducts: number;
  activeProducts: number;
  topSellingProductsCount: number;
  mostReviewedProduct: string;

  salesOverTime?: SalesOverTimeItem[];
  topSellingProducts?: TopSellingProductItem[];
  revenueByCategory?: RevenueByCategoryItem[];

  customerReviews?: CustomerReviewItem[];

  trafficVisitors?: TrafficVisitorItem[];

  topBuyers: number;
  totalSubscribers: number;
  onlineSubscribers: number;
  offlineSubscribersMoreThanMonth: number;
}

const PIE_COLORS = ["#f5b301", "#ff7a00", "#ff4d6d", "#7b61ff", "#38bdf8"];

const getStoredAdminUser = () => {
  const possibleKeys = ["admin_user", "adminUser", "lyrazone_admin_user"];

  for (const key of possibleKeys) {
    const raw = localStorage.getItem(key);

    if (!raw) continue;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  return null;
};

const LeafletResizeFix = () => {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 300);

    return () => clearTimeout(timer);
  }, [map]);

  return null;
};

const mapCenter: LatLngExpression = [20, 0];

const visitorMarkerOptions: PathOptions = {
  color: "#16a34a",
  fillColor: "#22c55e",
  fillOpacity: 0.75,
  weight: 2,
};

export default function Dashboard() {
  const USE_MOCK_DATA = false;
  const { formatDateToDMY } = useFormat();

  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  // const [selectedVisitor, setSelectedVisitor] =
  //   useState<TrafficVisitorItem | null>(null);
  const [selectedTrafficIp, setSelectedTrafficIp] =
    useState<TrafficVisitorItem | null>(null);

  const [trafficHourFilter, setTrafficHourFilter] = useState("");
  const [trafficWeekFilter, setTrafficWeekFilter] = useState("");
  const [trafficMonthFilter, setTrafficMonthFilter] = useState("");
  const [trafficYearFilter, setTrafficYearFilter] = useState("");

  const [activeTrafficSearchColumn, setActiveTrafficSearchColumn] = useState<
    string | null
  >(null);

  const [trafficSearch, setTrafficSearch] = useState<Record<string, string>>(
    {},
  );

  const buildDashboardQuery = () => {
    const params = new URLSearchParams();

    if (trafficHourFilter) params.append("trafficHours", trafficHourFilter);
    if (trafficWeekFilter) params.append("trafficWeeks", trafficWeekFilter);
    if (trafficMonthFilter) params.append("trafficMonths", trafficMonthFilter);
    if (trafficYearFilter) params.append("trafficYears", trafficYearFilter);

    return params.toString();
  };

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        if (USE_MOCK_DATA) {
          setData(mockDashboardData);
          setLoading(false);
          return;
        }

        const storedAdminUser = getStoredAdminUser();
        setAdminUser(storedAdminUser);

        const queryString = buildDashboardQuery();

        const res = await axios.get(
          // `${import.meta.env.VITE_LYRAZONE_BASE_URL}/admin/dashboard`,
          `${import.meta.env.VITE_LYRAZONE_BASE_URL}/admin/dashboard${
            queryString ? `?${queryString}` : ""
          }`,
          {
            withCredentials: true,
            params: {
              adminUserId: adminUser?.id,
              group_id: adminUser?.group_id,
            },
          },
        );

        console.log("Dashboard Data", res?.data?.data);

        const apiData = res?.data?.data || {};

        setData({
          dashboardScope: apiData.dashboardScope ?? "admin",
          vendorId: apiData.vendorId ?? null,

          todaysOrder: apiData.todaysOrder ?? 0,
          totalOrders: apiData.totalOrders ?? 0,
          todaysRevenue: Number(apiData.todaysRevenue ?? 0),
          totalRevenue: Number(apiData.totalRevenue ?? 0),

          totalProducts: apiData.totalProducts ?? 0,
          activeProducts: apiData.activeProducts ?? 0,
          topSellingProductsCount: apiData.topSellingProductsCount ?? 0,
          mostReviewedProduct: apiData.mostReviewedProduct ?? "-",

          trafficVisitors: (apiData.trafficVisitors ?? []).map((item: any) => ({
            ipAddress: item.ipAddress ?? "-",
            city: item.city ?? "-",
            country: item.country ?? "-",
            latitude: Number(item.latitude) || 0,
            longitude: Number(item.longitude) || 0,
            counter: Number(item.counter) || 0,
            visitedPages: Array.isArray(item.visitedPages)
              ? item.visitedPages
              : item.visitedPages
                ? String(item.visitedPages)
                    .split(",")
                    .map((page) => page.trim())
                : [],
            lastVisitedAt: item.lastVisitedAt ?? "",
          })),

          salesOverTime: apiData.salesOverTime ?? [],
          topSellingProducts: apiData.topSellingProducts ?? [],
          revenueByCategory: (apiData.revenueByCategory ?? []).map(
            (item: any) => ({
              name: item.name,
              value: Number(item.value) || 0,
            }),
          ),
          customerReviews: apiData.customerReviews ?? [],

          topBuyers: apiData.topBuyers ?? 0,
          totalSubscribers: apiData.totalSubscribers ?? 0,
          onlineSubscribers: apiData.onlineSubscribers ?? 0,
          offlineSubscribersMoreThanMonth:
            apiData.offlineSubscribersMoreThanMonth ?? 0,
        });
      } catch (error) {
        console.error("Dashboard fetch error:", error);
        setData(mockDashboardData);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [
    trafficHourFilter,
    trafficWeekFilter,
    trafficMonthFilter,
    trafficYearFilter,
  ]);

  const renderTrafficSearchHeader = (label: string, column: string) => (
    <th>
      <div className="traffic-th-filter">
        <span>{label}</span>
        <button
          type="button"
          onClick={() =>
            setActiveTrafficSearchColumn(
              activeTrafficSearchColumn === column ? null : column,
            )
          }
        >
          ▾
        </button>
      </div>

      {activeTrafficSearchColumn === column && (
        <input
          type="text"
          className="traffic-column-search"
          placeholder={`Search ${label}`}
          value={trafficSearch[column] || ""}
          onChange={(e) =>
            setTrafficSearch((prev) => ({
              ...prev,
              [column]: e.target.value,
            }))
          }
        />
      )}
    </th>
  );

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-MY", {
      style: "currency",
      currency: "MYR",
      minimumFractionDigits: 2,
    }).format(value);

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  if (!data) {
    return <div className="dashboard-loading">No dashboard data found.</div>;
  }

  const salesOverTime = data.salesOverTime || [];
  const topSellingProducts = data.topSellingProducts || [];
  const revenueByCategory = data.revenueByCategory || [];
  const customerReviews = data.customerReviews || [];
  const trafficVisitors = data.trafficVisitors || [];

  const filteredTrafficVisitors = trafficVisitors.filter((visitor) => {
    const citySearch = trafficSearch.city?.toLowerCase() || "";
    const countrySearch = trafficSearch.country?.toLowerCase() || "";
    const ipSearch = trafficSearch.ipAddress?.toLowerCase() || "";
    const pageSearch = trafficSearch.visitedPages?.toLowerCase() || "";

    const pagesText = (visitor.visitedPages || []).join(", ").toLowerCase();

    return (
      visitor.city.toLowerCase().includes(citySearch) &&
      visitor.country.toLowerCase().includes(countrySearch) &&
      visitor.ipAddress.toLowerCase().includes(ipSearch) &&
      pagesText.includes(pageSearch)
    );
  });

  const cityWiseTraffic = filteredTrafficVisitors.reduce(
    (acc, visitor) => {
      const cityKey = visitor.city || "-";

      if (!acc[cityKey]) {
        acc[cityKey] = {
          city: visitor.city,
          country: visitor.country,
          totalVisits: 0,
          visitors: [],
        };
      }

      acc[cityKey].totalVisits += visitor.counter;
      acc[cityKey].visitors.push(visitor);

      return acc;
    },
    {} as Record<
      string,
      {
        city: string;
        country: string;
        totalVisits: number;
        visitors: TrafficVisitorItem[];
      }
    >,
  );

  const groupedTrafficCities = Object.values(cityWiseTraffic).sort((a, b) =>
    a.city.localeCompare(b.city),
  );

  const ACTIVE_VISITOR_MINUTES = 5;

  const activeTrafficVisitors = trafficVisitors.filter((visitor) => {
    if (!visitor.lastVisitedAt) return false;

    const lastVisitedTime = new Date(visitor.lastVisitedAt).getTime();
    const now = Date.now();

    return now - lastVisitedTime <= ACTIVE_VISITOR_MINUTES * 60 * 1000;
  });

  return (
    <div className="lz-dashboard">
      <div className="dashboard-section-header">
        <h3>
          {data.dashboardScope === "vendor"
            ? `${adminUser?.name || "Vendor"}'s Shop Analytics`
            : "Application Analytics"}
        </h3>
      </div>
      {/* Section 1 */}
      <div className="dashboard-section dashboard-stats-grid">
        <div className="dashboard-stat-card">
          <span className="stat-title">Today&apos;s Order</span>
          <h3>{data.todaysOrder}</h3>
        </div>

        <div className="dashboard-stat-card">
          <span className="stat-title">Total Orders</span>
          <h3>{data.totalOrders}</h3>
        </div>

        <div className="dashboard-stat-card">
          <span className="stat-title">Today&apos;s Revenue</span>
          <h3>{formatCurrency(data.todaysRevenue)}</h3>
        </div>

        <div className="dashboard-stat-card">
          <span className="stat-title">Total Revenue</span>
          <h3>{formatCurrency(data.totalRevenue)}</h3>
        </div>
      </div>

      {/* Section 2 */}
      <div className="dashboard-section dashboard-icon-grid">
        <div className="dashboard-icon-card">
          <span className="icon-box">📦</span>
          <span>Total Products</span>
          <strong>{data.totalProducts}</strong>
        </div>

        <div className="dashboard-icon-card">
          <span className="icon-box">✅</span>
          <span>Active Products</span>
          <strong>{data.activeProducts}</strong>
        </div>

        <div className="dashboard-icon-card">
          <span className="icon-box">🔥</span>
          <span>Top 14 Selling Products</span>
          <strong>{data.topSellingProductsCount}</strong>
        </div>

        <div className="dashboard-icon-card">
          <span className="icon-box">⭐</span>
          <span>Most Reviewed Product</span>
          <strong className="dashboard-small-text">
            {data.mostReviewedProduct}
          </strong>
        </div>
      </div>

      {/* Section 3 */}
      <div className="dashboard-section dashboard-chart-grid">
        <div className="dashboard-chart-card">
          <h4>Sales Over Time</h4>
          <div className="chart-wrapper">
            <ResponsiveContainer
              width="100%"
              height={280}
              style={{ fontSize: "10px" }}
            >
              <LineChart data={salesOverTime}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="sales"
                  stroke="#d4a017"
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="dashboard-chart-card">
          <h4>5 Top Selling Products</h4>
          <div className="chart-wrapper">
            <ResponsiveContainer
              width="100%"
              height={280}
              style={{ fontSize: "10px" }}
            >
              <BarChart data={topSellingProducts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="sold" fill="#d4b106" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="dashboard-chart-card">
          <h4>Revenue by Category</h4>
          <div className="chart-wrapper">
            <ResponsiveContainer
              width="100%"
              height={280}
              style={{ fontSize: "10px" }}
            >
              <PieChart>
                <Pie
                  data={revenueByCategory}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  label
                >
                  {revenueByCategory.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Section 4 */}
      <div className="dashboard-section dashboard-table-card">
        <div className="dashboard-section-header">
          <h4>Customer Review</h4>
        </div>

        <div className="dashboard-table-wrapper">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Subscriber ID</th>
                <th>Subscriber Name</th>
                <th>Product Name</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {customerReviews.length > 0 ? (
                customerReviews.map((review, index) => (
                  <tr key={index}>
                    <td>{review.subscriberId}</td>
                    <td>{review.subscriberName}</td>
                    <td>{review.productName}</td>
                    <td>{review.review}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="dashboard-empty-row">
                    No customer reviews found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Website Traffic Analytics */}
      <div className="dashboard-section dashboard-traffic-card">
        <div className="dashboard-section-header">
          <h4>Website Traffic Analytics</h4>
        </div>

        <div className="traffic-map-wrapper">
          <MapContainer
            center={mapCenter}
            zoom={2}
            minZoom={2}
            scrollWheelZoom={true}
            zoomControl={false}
            className="traffic-leaflet-map"
          >
            <LeafletResizeFix />

            <ZoomControl position="bottomright" />

            <TileLayer
              attribution="&copy; OpenStreetMap contributors"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              // url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />

            {activeTrafficVisitors.map((visitor, index) => {
              const markerCenter: LatLngExpression = [
                Number(visitor.latitude),
                Number(visitor.longitude),
              ];

              return (
                <CircleMarker
                  key={`${visitor.ipAddress}-${index}`}
                  center={markerCenter}
                  radius={10}
                  pathOptions={visitorMarkerOptions}
                  eventHandlers={{
                    click: () => setSelectedTrafficIp(visitor),
                    mouseover: (event) => {
                      event.target.openPopup();
                    },
                  }}
                >
                  <Popup>
                    <div className="traffic-popup">
                      <h4>{visitor.city}</h4>

                      <div className="traffic-popup-row">
                        <span>Country</span>
                        <strong>{visitor.country}</strong>
                      </div>

                      <div className="traffic-popup-row">
                        <span>IP Address</span>
                        <strong>{visitor.ipAddress}</strong>
                      </div>

                      <div className="traffic-popup-row">
                        <span>Visitors</span>
                        <strong>{visitor.counter}</strong>
                      </div>

                      <div className="traffic-popup-row">
                        <span>Status</span>
                        <strong>Active now</strong>
                      </div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>
        <div className="traffic-list-grid">
          {groupedTrafficCities.length > 0 ? (
            groupedTrafficCities.map((group) => (
              <div
                key={`${group.city}-${group.country}`}
                className="traffic-city-card"
              >
                <strong>{group.city}</strong>
                <span>{group.country}</span>
                <b>{group.totalVisits} visits</b>

                <div className="traffic-ip-list">
                  {group.visitors.map((visitor, index) => (
                    <button
                      key={`${visitor.ipAddress}-${index}`}
                      type="button"
                      className="traffic-ip-chip"
                      onClick={() => setSelectedTrafficIp(visitor)}
                    >
                      {visitor.ipAddress}
                      <small>{visitor.counter}</small>
                    </button>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="dashboard-empty-row">No traffic data found.</div>
          )}
        </div>

        <div className="traffic-filter-bar">
          <select
            value={trafficHourFilter}
            onChange={(e) => {
              setTrafficHourFilter(e.target.value);
              setTrafficWeekFilter("");
              setTrafficMonthFilter("");
              setTrafficYearFilter("");
            }}
          >
            <option value="">Hour Filter</option>
            <option value="1">This Hour</option>
            <option value="24">24 Hours</option>
            <option value="36">36 Hours</option>
            <option value="48">48 Hours</option>
          </select>

          <select
            value={trafficWeekFilter}
            onChange={(e) => {
              setTrafficWeekFilter(e.target.value);
              setTrafficHourFilter("");
              setTrafficMonthFilter("");
              setTrafficYearFilter("");
            }}
          >
            <option value="">Week Filter</option>
            <option value="1">This Week</option>
            <option value="2">Two Weeks</option>
            <option value="3">Three Weeks</option>
          </select>

          <select
            value={trafficMonthFilter}
            onChange={(e) => {
              setTrafficMonthFilter(e.target.value);
              setTrafficHourFilter("");
              setTrafficWeekFilter("");
              setTrafficYearFilter("");
            }}
          >
            <option value="">Month Filter</option>
            {Array.from({ length: 12 }, (_, index) => (
              <option key={index + 1} value={index + 1}>
                {index === 0 ? "This Month" : `${index + 1} Months`}
              </option>
            ))}
          </select>

          <select
            value={trafficYearFilter}
            onChange={(e) => {
              setTrafficYearFilter(e.target.value);
              setTrafficHourFilter("");
              setTrafficWeekFilter("");
              setTrafficMonthFilter("");
            }}
          >
            <option value="">Year Filter</option>
            {Array.from({ length: 10 }, (_, index) => (
              <option key={index + 1} value={index + 1}>
                {index === 0 ? "This Year" : `${index + 1} Years`}
              </option>
            ))}
          </select>
        </div>
        <div className="traffic-table-wrapper">
          <table className="traffic-table">
            <thead>
              <tr>
                <th>#</th>
                {renderTrafficSearchHeader("City", "city")}
                {renderTrafficSearchHeader("Country", "country")}
                {renderTrafficSearchHeader("IP Address", "ipAddress")}
                <th>Latitude</th>
                <th>Longitude</th>
                {renderTrafficSearchHeader("Visited Page(s)", "visitedPages")}
                <th>Last Visited At</th>
                <th>Visits</th>
              </tr>
            </thead>

            <tbody>
              {filteredTrafficVisitors.length > 0 ? (
                filteredTrafficVisitors.map((visitor, index) => (
                  <tr
                    key={`${visitor.ipAddress}-${visitor.city}-table-${index}`}
                    onClick={() => setSelectedTrafficIp(visitor)}
                  >
                    <td>{index + 1}</td>
                    <td>{visitor.city}</td>
                    <td>{visitor.country}</td>
                    <td>{visitor.ipAddress}</td>
                    <td>{visitor.latitude}</td>
                    <td>{visitor.longitude}</td>
                    <td>
                      {(visitor.visitedPages || []).length > 0
                        ? visitor.visitedPages?.map((page, pageIndex) => (
                            <div
                              key={`${page}-${pageIndex}`}
                              className="traffic-page-link"
                            >
                              {page}
                            </div>
                          ))
                        : "-"}
                    </td>
                    <td>{formatDateToDMY(visitor.lastVisitedAt)}</td>
                    <td>{visitor.counter}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="dashboard-empty-row">
                    No traffic visitors found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedTrafficIp && (
        <div
          className="traffic-modal-overlay"
          onClick={() => setSelectedTrafficIp(null)}
        >
          <div
            className="traffic-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="traffic-modal-close"
              onClick={() => setSelectedTrafficIp(null)}
            >
              ×
            </button>

            <h3>{selectedTrafficIp.city}</h3>
            <p>{selectedTrafficIp.country}</p>

            <div className="traffic-modal-details">
              <span>IP Address</span>
              <strong>{selectedTrafficIp.ipAddress}</strong>

              <span>Visitor Counter</span>
              <strong>{selectedTrafficIp.counter}</strong>

              <span>Latitude</span>
              <strong>{selectedTrafficIp.latitude}</strong>

              <span>Longitude</span>
              <strong>{selectedTrafficIp.longitude}</strong>

              <span>Visited Page(s)</span>
              <strong>
                {(selectedTrafficIp.visitedPages || []).length > 0
                  ? selectedTrafficIp.visitedPages?.join(", ")
                  : "-"}
              </strong>
            </div>
          </div>
        </div>
      )}

      {/* Section 5 */}
      <div className="dashboard-section dashboard-stats-grid bottom-stats">
        <div className="dashboard-stat-card">
          <span className="stat-title">Top Buyers</span>
          <h3>{data.topBuyers}</h3>
        </div>

        <div className="dashboard-stat-card">
          <span className="stat-title">Total Subscribers</span>
          <h3>{data.totalSubscribers}</h3>
        </div>

        <div className="dashboard-stat-card">
          <span className="stat-title">Online Subscribers</span>
          <h3>{data.onlineSubscribers}</h3>
        </div>

        <div className="dashboard-stat-card">
          <span className="stat-title">Offline Subscribers &gt; 1 Month</span>
          <h3>{data.offlineSubscribersMoreThanMonth}</h3>
        </div>
      </div>
    </div>
  );
}

const mockDashboardData: DashboardData = {
  todaysOrder: 12,
  totalOrders: 384,
  todaysRevenue: 1230,
  totalRevenue: 48230,

  totalProducts: 320,
  activeProducts: 286,
  topSellingProductsCount: 14,
  mostReviewedProduct: "Wireless Earbuds",

  salesOverTime: [
    { month: "Jan", sales: 12000 },
    { month: "Feb", sales: 15000 },
    { month: "Mar", sales: 18000 },
    { month: "Apr", sales: 17000 },
    { month: "May", sales: 21000 },
    { month: "Jun", sales: 22500 },
  ],

  topSellingProducts: [
    { name: "Item A", sold: 150 },
    { name: "Item B", sold: 90 },
    { name: "Item C", sold: 200 },
    { name: "Item D", sold: 60 },
    { name: "Item E", sold: 120 },
  ],

  revenueByCategory: [
    { name: "Electronics", value: 40 },
    { name: "Clothing", value: 24 },
    { name: "Home", value: 16 },
    { name: "Books", value: 8 },
    { name: "Sports", value: 12 },
  ],

  customerReviews: [
    {
      subscriberId: "SUB001",
      subscriberName: "Ali Hassan",
      productName: "Wireless Earbuds",
      review: "Very good sound quality and fast delivery.",
    },
    {
      subscriberId: "SUB002",
      subscriberName: "Sarah Lim",
      productName: "Smart Watch",
      review: "Nice design and battery life is decent.",
    },
    {
      subscriberId: "SUB003",
      subscriberName: "John Tan",
      productName: "Bluetooth Speaker",
      review: "Excellent bass and clear voice output.",
    },
    {
      subscriberId: "SUB004",
      subscriberName: "Aina Yusuf",
      productName: "Laptop Stand",
      review: "Strong material and useful for daily work.",
    },
  ],

  trafficVisitors: [
    {
      ipAddress: "103.6.198.10",
      city: "Kuala Lumpur",
      country: "Malaysia",
      latitude: 3.139,
      longitude: 101.6869,
      counter: 18,
    },
    {
      ipAddress: "175.139.120.55",
      city: "Rawang",
      country: "Malaysia",
      latitude: 3.3213,
      longitude: 101.5767,
      counter: 7,
    },
    {
      ipAddress: "8.8.8.8",
      city: "Mountain View",
      country: "USA",
      latitude: 37.3861,
      longitude: -122.0839,
      counter: 3,
    },
  ],

  topBuyers: 25,
  totalSubscribers: 1240,
  onlineSubscribers: 184,
  offlineSubscribersMoreThanMonth: 76,
};
